/**
 * Kalender med aflysninger, flytninger og ferielukning — og indbakken med
 * læst/ulæst og svar til afsenderen.
 *
 * KALENDEREN VISER OGSÅ DET, DER IKKE SKER
 * En aflyst træning fjernes ikke fra listen. Den bliver stående, streget ud,
 * med begrundelsen. Fjernede vi den, ville forælderen ikke kunne se
 * forskel på «aflyst» og «jeg har husket forkert» — og så ville han møde op.
 *
 * Begge sider bruger optimistiske opdateringer, der RULLES TILBAGE ved fejl
 * (§4). Afbud og læst-status skrives til serveren og hentes derefter igen,
 * så visningen aldrig lover mere end det gemte.
 */

import { useState } from 'react';
import { Link } from '../lib/router.js';
import { antal, api, formatTime, capitalise,
  relativeDay, formatShortDate, shortName } from '../lib/api.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  ChipFilter,
  EmptyState,
  Field,
  Meta,
  Notice,
  PageHead,
  useAction,
  useAsync,
  type BadgeKind,
} from '../components/ui.js';
import { Icon } from '../components/icons.js';
import { ABSENCE_REASONS, ABSENCE_REASON_LABELS, REPLY_BODY_MAX } from '../../shared/domain.js';

interface FamilyMember {
  personKey: string;
  name: string;
  relation: 'self' | 'child';
}

type SessionStatus = 'scheduled' | 'cancelled' | 'moved' | 'closed';

interface CalendarEntry {
  sessionId: string;
  date: string;
  startsAt: string;
  endsAt: string;
  status: SessionStatus;
  reason: string;
  movedFromDate: string | null;
  isExtra: boolean;
  changedByName: string;
  teamId: string;
  teamName: string;
  teamSlug: string;
  department: string;
  location: string;
  personKey: string;
  personName: string;
  absence: { id: string; reason: string; reasonLabel: string; note: string } | null;
  canReportAbsence: boolean;
}

export interface ClubEvent {
  id: string;
  title: string;
  description: string;
  location: string;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  audienceLabel: string;
  status: 'scheduled' | 'cancelled';
  cancelReason: string;
  createdByName: string;
  helperSlots: number;
  helpersNeeded: number;
  signupEnabled: boolean;
  signupDeadline: string | null;
  /** Pris i ØRE. 0 = gratis. */
  priceOere: number;
  paymentUrl: string;
}

interface CalendarData {
  entries: CalendarEntry[];
  events: ClubEvent[];
  needsReview: Array<{ teamId: string; teamName: string; personName: string; reason: string }>;
  season: { id: string; name: string; startDate: string; endDate: string } | null;
  seasonEnded: boolean;
  family: FamilyMember[];
  note: string;
}

/* ------------------------------------------------------------------ */
/* Kalender                                                            */
/* ------------------------------------------------------------------ */

export function CalendarPage(): JSX.Element {
  const [person, setPerson] = useState('alle');
  const state = useAsync<CalendarData>(
    () => api.get(`/api/calendar?person=${encodeURIComponent(person)}`),
    [person],
  );
  const [openEntry, setOpenEntry] = useState<string | null>(null);

  return (
    <div className="stack">
      <PageHead title="Kalender" sub="Kommende træninger for din familie" />
      <AsyncSection state={state}>
        {(data) => (
          <>
            {data.family.length > 1 ? (
              <ChipFilter
                label="Vis træninger for"
                value={person}
                onChange={setPerson}
                options={[
                  { value: 'alle', label: 'Alle' },
                  ...data.family.map((f) => ({
                    value: f.personKey,
                    label: shortName(
                      f.name,
                      data.family.map((x) => x.name),
                    ),
                  })),
                ]}
              />
            ) : null}

            {data.seasonEnded ? (
              <Notice kind="warning">
                Sæsonen er slut, og der er ikke oprettet en ny endnu. Kalenderen fyldes op igen, så
                snart foreningen opretter næste sæson.
              </Notice>
            ) : null}

            {/* Hold, hvor kildedata er modstridende. Vi gætter ALDRIG på en
                dato — så hellere sige det højt end vise noget forkert. */}
            {data.needsReview.map((item) => (
              <Notice key={`${item.teamId}-${item.personName}`} kind="warning">
                <strong>{item.teamName}: kalenderen er ikke klar</strong>
                <p className="small mt-1">
                  {item.reason} Foreningen skal bekræfte den rigtige dato, før træningerne kan
                  vises. Indtil da kan der ikke meldes afbud til holdet her.
                </p>
              </Notice>
            ))}

            {/*
              TRÆNINGER OG ARRANGEMENTER FLETTES HER, PÅ SKÆRMEN.
              Serveren sender dem som to lister, fordi de er to forskellige
              ting: en træning hører til én person i familien og kan meldes
              afbud til, et arrangement gør ingen af delene. Men FOR DEN, DER
              KIGGER, er det én kalender — «hvad sker der de næste uger» — så
              de sorteres sammen efter tidspunkt.

              Personfilteret rører kun træningerne. Opvisningen er for hele
              familien, uanset hvilket barn man har valgt.
            */}
            {data.entries.length === 0 && data.events.length === 0 ? (
              <div className="card">
                <EmptyState title="Ingen kommende aktiviteter" icon="calendar">
                  {person === 'alle'
                    ? 'Der er ikke registreret kommende træninger eller arrangementer for din familie.'
                    : 'Der er ikke registreret kommende træninger for den valgte person.'}
                </EmptyState>
              </div>
            ) : (
              <div className="stack">
                {flet(data.entries, data.events).map((item) =>
                  item.kind === 'event' ? (
                    <EventCard key={`e-${item.event.id}`} event={item.event} />
                  ) : (
                    <CalendarCard
                      key={`${item.entry.sessionId}-${item.entry.personKey}`}
                      entry={item.entry}
                      open={openEntry === `${item.entry.sessionId}-${item.entry.personKey}`}
                      onToggle={(id) => setOpenEntry((prev) => (prev === id ? null : id))}
                      onChanged={state.reload}
                    />
                  ),
                )}
              </div>
            )}
            <p className="tiny muted">{data.note}</p>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

type FlettetPost =
  | { kind: 'session'; startsAt: string; entry: CalendarEntry }
  | { kind: 'event'; startsAt: string; event: ClubEvent };

/** Sorterer træninger og arrangementer sammen efter tidspunkt. */
function flet(entries: CalendarEntry[], events: ClubEvent[]): FlettetPost[] {
  const alle: FlettetPost[] = [
    ...entries.map((entry) => ({ kind: 'session' as const, startsAt: entry.startsAt, entry })),
    ...events.map((event) => ({ kind: 'event' as const, startsAt: event.startsAt, event })),
  ];
  return alle.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * Et arrangement i familiens kalender.
 *
 * Bevidst SMALLERE end et træningskort: der er ikke noget at melde afbud
 * til, ingen instruktør at skrive til og ingen person at vælge. Et kort med
 * knapper, der ikke gør noget, er værre end et kort uden.
 *
 * Et aflyst arrangement bliver stående med sin begrundelse — samme regel som
 * ved en aflyst træning, og af samme grund: forsvandt det, kunne man ikke
 * skelne «aflyst» fra «jeg har husket forkert».
 */
function EventCard({ event }: { event: ClubEvent }): JSX.Element {
  const aflyst = event.status === 'cancelled';
  const [visTilmelding, setVisTilmelding] = useState(false);
  return (
    <article className={`card${aflyst ? ' card--muted' : ''}`}>
      <div className="row row--between">
        <span className="small">
          <strong style={aflyst ? { textDecoration: 'line-through' } : undefined}>{event.title}</strong>
        </span>
        <span className="row" style={{ gap: 6 }}>
          {aflyst ? <Badge kind="warning">Aflyst</Badge> : null}
          <Badge kind="muted">Arrangement</Badge>
        </span>
      </div>

      <p className="small muted mt-1">
        {capitalise(relativeDay(event.startsAt))}
        {event.allDay ? '' : ` kl. ${formatTime(event.startsAt)}`}
        {event.endsAt && !event.allDay ? `–${formatTime(event.endsAt)}` : ''}
        {event.location ? ` · ${event.location}` : ''}
      </p>

      {aflyst ? (
        <p className="small mt-2">
          <strong>Aflyst:</strong> {event.cancelReason}
        </p>
      ) : null}

      {event.description ? <p className="small mt-2">{event.description}</p> : null}

      {/*
        OPFORDRINGEN STÅR HER — ved arrangementet, ikke på en side, man
        skal finde. Det er dét, der gør forskellen mellem «de samme fem
        laver alting» og at nogen melder sig: man skal se, at der mangler
        nogen, i samme øjeblik man ser, at der er en opvisning.

        Ét tal, ikke en liste. Tolv opgaver er en mur; «vi mangler 4» er
        noget, man kan overskue at sige ja til.
      */}
      {!aflyst && event.helpersNeeded > 0 ? (
        <div className="notice notice--info mt-3">
          <p className="small">
            <strong>
              Vi mangler {antal(event.helpersNeeded, 'hjælper', 'hjælpere')} til det her.
            </strong>
          </p>
          <p className="mt-2">
            <Link className="btn btn--secondary btn--small" to="/frivillige">
              Se opgaverne
            </Link>
          </p>
        </div>
      ) : null}

      {!aflyst && event.helperSlots > 0 && event.helpersNeeded === 0 ? (
        <p className="tiny muted mt-2">Alle hjælpere er på plads. Tak.</p>
      ) : null}

      {!aflyst && event.signupEnabled ? (
        <Tilmelding
          event={event}
          aaben={visTilmelding}
          onAaben={() => setVisTilmelding(true)}
          onLuk={() => setVisTilmelding(false)}
        />
      ) : null}

      <p className="tiny muted mt-2">
        {event.audienceLabel} · oprettet af {event.createdByName}
      </p>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Tilmelding                                                          */
/* ------------------------------------------------------------------ */

interface SignupVisning {
  enabled: boolean;
  deadline: string | null;
  priceOere: number;
  paymentUrl: string;
  closed: boolean;
  closedReason: string;
  mine: {
    id: string;
    adults: number;
    people: Array<{ memberId: string; name: string }>;
    paidAt: string | null;
  } | null;
  family: Array<{ memberId: string; name: string }>;
  summary: { tilmeldinger: number; deltagere: number; voksne: number; ialt: number };
}

/** «50 kr.» — prisen ligger i øre, så 49,50 ikke bliver til 49,5. */
function pris(oere: number): string {
  return oere % 100 === 0 ? `${oere / 100} kr.` : `${(oere / 100).toFixed(2).replace('.', ',')} kr.`;
}

/**
 * Familiens tilmelding til et arrangement.
 *
 * DET, DER ER VÆRD AT LÆGGE MÆRKE TIL: betalingen er et LINK ud af appen.
 * Appen får aldrig at vide, om der blev betalt — arrangøren sætter krydset
 * bagefter. Derfor står der «vi krydser af, når vi kan se betalingen», og
 * ikke noget, der lyder som en kvittering.
 */
function Tilmelding({
  event,
  aaben,
  onAaben,
  onLuk,
}: {
  event: ClubEvent;
  aaben: boolean;
  onAaben: () => void;
  onLuk: () => void;
}): JSX.Element {
  const state = useAsync<SignupVisning>(() => api.get(`/api/events/${event.id}/tilmelding`));
  const action = useAction();
  const [valgte, setValgte] = useState<string[]>([]);
  const [voksne, setVoksne] = useState(0);
  const [klar, setKlar] = useState(false);

  const start = (data: SignupVisning): void => {
    setValgte(data.mine ? data.mine.people.map((p) => p.memberId) : data.family.map((f) => f.memberId));
    setVoksne(data.mine?.adults ?? 0);
    setKlar(true);
    onAaben();
  };

  const gem = (): void => {
    void action.run(async () => {
      await api.post(`/api/events/${event.id}/tilmelding`, { memberIds: valgte, adults: voksne });
      action.setSuccess('I er meldt til.');
      setKlar(false);
      onLuk();
      state.reload();
    });
  };

  const meldFra = (): void => {
    void action.run(async () => {
      await api.del(`/api/events/${event.id}/tilmelding`);
      action.setSuccess('I er meldt fra igen.');
      setKlar(false);
      onLuk();
      state.reload();
    });
  };

  return (
    <div className="notice mt-3">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state} skeletonLines={2}>
        {(data) => (
          <>
            <p className="small">
              <strong>Tilmelding</strong>
              {data.priceOere > 0 ? <> · {pris(data.priceOere)} pr. deltager</> : null}
              {data.deadline ? (
                <span className="muted"> · frist {formatShortDate(data.deadline)}</span>
              ) : null}
            </p>

            {data.mine ? (
              <p className="small mt-1">
                I er tilmeldt:{' '}
                <strong>
                  {data.mine.people
                    .map((p) => shortName(p.name, data.family.map((f) => f.name)))
                    .join(', ') || 'ingen fra familien'}
                </strong>
                {data.mine.adults > 0 ? ` + ${antal(data.mine.adults, 'voksen', 'voksne')}` : ''}.
              </p>
            ) : (
              <p className="small mt-1 muted">
                {data.summary.ialt > 0
                  ? `${antal(data.summary.ialt, 'er', 'er')} meldt til indtil videre.`
                  : 'Ingen har meldt sig endnu.'}
              </p>
            )}

            {data.priceOere > 0 && data.mine ? (
              data.mine.paidAt ? (
                <p className="small mt-2">
                  <strong>Betalingen er registreret.</strong>
                </p>
              ) : (
                <div className="mt-2">
                  <p>
                    <a
                      className="btn btn--small"
                      href={data.paymentUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Betal {pris(data.priceOere)} med MobilePay
                    </a>
                  </p>
                  {/*
                    INGEN KVITTERING. Appen får ikke besked fra MobilePay, så
                    den ved ikke, om der blev betalt. At skrive noget andet
                    ville være at love en sikkerhed, der ikke er der.
                  */}
                  <p className="tiny muted mt-1">
                    Skriv deltagerens navn i beskeden, når du betaler. Vi krydser af, når vi kan
                    se betalingen — det sker ikke automatisk.
                  </p>
                </div>
              )
            ) : null}

            {data.closed ? (
              <p className="tiny muted mt-2">{data.closedReason}</p>
            ) : aaben && klar ? (
              <div className="stack stack--tight mt-2">
                <Field label="Hvem kommer?" htmlFor={`tm-${event.id}`}>
                  <div id={`tm-${event.id}`} className="stack stack--tight">
                    {data.family.length === 0 ? (
                      <p className="tiny muted">
                        Din profil er ikke forbundet med et medlem endnu. Du kan stadig melde
                        voksne til.
                      </p>
                    ) : null}
                    {data.family.map((f) => (
                      <label key={f.memberId} className="row" style={{ gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={valgte.includes(f.memberId)}
                          onChange={(e) =>
                            setValgte((v) =>
                              (e.target as HTMLInputElement).checked
                                ? [...v, f.memberId]
                                : v.filter((x) => x !== f.memberId),
                            )
                          }
                        />
                        <span className="small">{f.name}</span>
                      </label>
                    ))}
                  </div>
                </Field>

                <Field
                  label="Voksne med"
                  htmlFor={`vo-${event.id}`}
                  hint="Til en opvisning er det halvdelen af salen — tallet bruges til at stille stole frem."
                >
                  <input
                    id={`vo-${event.id}`}
                    type="number"
                    min={0}
                    max={20}
                    value={voksne}
                    onChange={(e) => setVoksne(Number((e.target as HTMLInputElement).value) || 0)}
                  />
                </Field>

                <div className="btn-group">
                  <ActionButton
                    className="btn btn--small"
                    pending={action.pending}
                    disabled={valgte.length === 0 && voksne === 0}
                    onClick={gem}
                  >
                    {data.mine ? 'Gem ændringen' : 'Meld til'}
                  </ActionButton>
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      setKlar(false);
                      onLuk();
                    }}
                  >
                    Fortryd
                  </button>
                </div>
              </div>
            ) : (
              <div className="btn-group mt-2">
                <button
                  type="button"
                  className={`btn btn--small${data.mine ? ' btn--secondary' : ''}`}
                  onClick={() => start(data)}
                >
                  {data.mine ? 'Ret tilmeldingen' : 'Meld til'}
                </button>
                {data.mine ? (
                  <ActionButton
                    className="btn btn--ghost btn--small"
                    pending={action.pending}
                    onClick={meldFra}
                  >
                    Meld fra
                  </ActionButton>
                ) : null}
              </div>
            )}
          </>
        )}
      </AsyncSection>
    </div>
  );
}

const STATUS_BADGE: Record<SessionStatus, { label: string; kind: BadgeKind } | null> = {
  scheduled: null,
  cancelled: { label: 'Aflyst', kind: 'warning' },
  moved: { label: 'Flyttet', kind: 'warning' },
  closed: { label: 'Ferielukket', kind: 'muted' },
};

function CalendarCard({
  entry,
  open,
  onToggle,
  onChanged,
}: {
  entry: CalendarEntry;
  open: boolean;
  onToggle: (id: string) => void;
  onChanged: () => void;
}): JSX.Element {
  const id = `${entry.sessionId}-${entry.personKey}`;
  const action = useAction();
  const [reason, setReason] = useState<string>('sygdom');
  const [note, setNote] = useState('');

  const submitAbsence = (): void => {
    void action.run(async () => {
      await api.post('/api/absences', {
        personKey: entry.personKey,
        sessionId: entry.sessionId,
        reason,
        note,
      });
      action.setSuccess('Afbuddet er registreret.');
      setNote('');
      onToggle(id);
      onChanged();
    });
  };

  const removeAbsence = (): void => {
    void action.run(async () => {
      if (!entry.absence) throw new Error('Afbuddet findes ikke længere.');
      await api.del(`/api/absences/${entry.absence.id}`);
      action.setSuccess('Afbuddet er fjernet.');
      onChanged();
    });
  };

  const badge = STATUS_BADGE[entry.status];
  const off = entry.status === 'cancelled' || entry.status === 'closed';

  return (
    <article
      className={`card dept-stripe dept-stripe--${entry.department}${off ? ' card--off' : ''}`}
    >
      {/*
        RÆKKEFØLGEN ER VENDT OM.
        Før stod personens navn som overskrift og holdet som en lille grå
        linje under. Men man kender allerede sit barns navn — man scanner
        efter DATOEN og efter HVILKET hold. Nu står datoen først og størst,
        derefter holdet, og navnet sidst som den oplysning, der binder de to
        sammen. Familiefiltret ovenfor gør det desuden ofte overflødigt.
      */}
      <p className="calcard__when">
        <span className={`calcard__date${off ? ' strike' : ''}`}>
          {relativeDay(entry.startsAt)}
        </span>
        <span className="calcard__time">
          {formatTime(entry.startsAt)}–{formatTime(entry.endsAt)}
        </span>
      </p>
      <h3 className="calcard__team">{entry.teamName}</h3>
      <p className="calcard__who">{entry.personName}</p>

      {badge || entry.isExtra ? (
        <div className="row mt-2" style={{ gap: 6 }}>
          {badge ? (
            <Badge kind={badge.kind} icon={entry.status === 'cancelled' ? 'absence' : 'clock'}>
              {badge.label}
            </Badge>
          ) : null}
          {entry.isExtra ? <Badge icon="plus">Ekstra træning</Badge> : null}
        </div>
      ) : null}

      <div className="meta mt-2">
        <Meta icon="place" label="Sted">
          {entry.location}
        </Meta>
      </div>

      {/* Begrundelsen er det vigtigste på kortet, når noget er ændret.
          Den skrives af et menneske og vises, som den er. */}
      {entry.status !== 'scheduled' && entry.reason ? (
        <p className="small mt-2">
          <strong>{entry.reason}</strong>
          {entry.changedByName ? (
            <span className="tiny muted"> — {entry.changedByName}</span>
          ) : null}
        </p>
      ) : null}

      {entry.status === 'moved' && entry.movedFromDate ? (
        <p className="tiny muted mt-1">
          Flyttet fra {formatShortDate(`${entry.movedFromDate}T12:00:00Z`)}.
        </p>
      ) : null}

      {action.error ? (
        <div className="mt-3">
          <Notice kind="error">{action.error}</Notice>
        </div>
      ) : null}

      {entry.absence ? (
        <div className="row row--between mt-3">
          <Badge kind="warning" icon="absence">
            Afbud: {entry.absence.reasonLabel}
            {entry.absence.note ? ` — ${entry.absence.note}` : ''}
          </Badge>
          {entry.canReportAbsence ? (
            <ActionButton
              className="btn btn--ghost btn--small"
              pending={action.pending}
              onClick={removeAbsence}
            >
              Fjern afbud
            </ActionButton>
          ) : null}
        </div>
      ) : !entry.canReportAbsence ? (
        <p className="tiny muted mt-3">
          {entry.status === 'cancelled'
            ? 'Træningen er aflyst — du skal ikke melde afbud.'
            : entry.status === 'closed'
              ? 'Der er ikke træning denne dag.'
              : 'Der kan ikke meldes afbud til denne træning.'}
        </p>
      ) : open ? (
        <div className="stack stack--tight mt-3">
          <Field label="Årsag" htmlFor={`reason-${id}`}>
            <select
              id={`reason-${id}`}
              value={reason}
              onChange={(e) => setReason((e.target as HTMLSelectElement).value)}
            >
              {ABSENCE_REASONS.map((value) => (
                <option key={value} value={value}>
                  {ABSENCE_REASON_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
          {/*
            EN LINJE OM, HVAD DER *IKKE* SKAL SKRIVES.
            Feltet er fritekst om et barn, og det bliver stående. Uden den
            her linje skriver velmenende forældre og instruktører detaljer om
            helbred og trivsel, som foreningen hverken har brug for eller bør
            opbevare. Grunden til afbuddet er allerede valgt ovenfor.
          */}
          <Field
            label="Bemærkning (valgfri)"
            htmlFor={`note-${id}`}
            hint="Kun det praktiske — fx «kommer 20 min. senere». Skriv ikke om helbred eller andet privat; instruktøren har ikke brug for det."
          >
            <input
              id={`note-${id}`}
              type="text"
              maxLength={200}
              value={note}
              onChange={(e) => setNote((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="btn-group">
            <ActionButton pending={action.pending} onClick={submitAbsence}>
              Meld afbud
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => onToggle(id)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : (
        /*
          «Meld afbud» er en TYDELIG, men sekundær handling. Den var en
          kantet knap før og fyldte lige så meget som selve træningen — på
          hvert eneste kort, selv om de fleste bruger den sjældent. Nu er
          den rød tekst med pictogram: umulig at overse, men den er ikke
          det, øjet lander på først.
        */
        <p className="calcard__action">
          <button type="button" className="btn btn--ghost btn--small" onClick={() => onToggle(id)}>
            <Icon name="absence" size={17} />
            Meld afbud
          </button>
        </p>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Indbakke                                                            */
/* ------------------------------------------------------------------ */

interface InboxItem {
  id: string;
  title: string;
  body: string;
  audienceLabel: string;
  priority: 'normal' | 'important';
  publishedAt: string;
  senderName: string;
  read: boolean;
  relatedPersonKeys: string[];
  sentByMe: boolean;
  repliesEnabled: boolean;
  replyCount: number;
  unreadReplies: number;
}

export function MessagesPage(): JSX.Element {
  const [person, setPerson] = useState('alle');
  const state = useAsync<{
    announcements: InboxItem[];
    unread: number;
    family: FamilyMember[];
    note: string;
  }>(() => api.get(`/api/announcements?person=${encodeURIComponent(person)}`), [person]);

  // Optimistisk læst-status. Rulles tilbage ved fejl.
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  /* Én besked ad gangen er foldet ud. En indbakke, hvor alt står åbent, er
     ikke en indbakke — det er en væg af tekst. */
  const [openMessage, setOpenMessage] = useState<string | null>(null);

  const toggleRead = async (item: InboxItem): Promise<void> => {
    const next = !(optimistic[item.id] ?? item.read);
    setOptimistic((prev) => ({ ...prev, [item.id]: next }));
    setError(null);
    try {
      await api.post('/api/announcement-reads', { announcementId: item.id, read: next });
    } catch {
      // Rul tilbage: visningen må ikke påstå noget, serveren ikke har gemt.
      setOptimistic((prev) => {
        const copy = { ...prev };
        delete copy[item.id];
        return copy;
      });
      setError('Status kunne ikke gemmes. Prøv igen.');
    }
  };

  return (
    <div className="stack">
      <PageHead title="Beskeder" />
      {error ? <Notice kind="error">{error}</Notice> : null}
      <AsyncSection state={state}>
        {(data) => (
          <>
            {data.family.length > 1 ? (
              <ChipFilter
                label="Vis beskeder for"
                value={person}
                onChange={setPerson}
                options={[
                  { value: 'alle', label: 'Alle' },
                  ...data.family.map((f) => ({
                    value: f.personKey,
                    label: shortName(
                      f.name,
                      data.family.map((x) => x.name),
                    ),
                  })),
                ]}
              />
            ) : null}

            {data.announcements.length === 0 ? (
              <div className="card">
                <EmptyState title="Ingen beskeder" icon="message">
                  {person === 'alle'
                    ? 'Der er ingen beskeder til dig endnu.'
                    : 'Der er ingen beskeder, der vedrører den valgte person. Private beskeder til din konto vises kun under «Alle».'}
                </EmptyState>
              </div>
            ) : (
              data.announcements.map((item) => {
                const read = optimistic[item.id] ?? item.read;
                const expanded = openMessage === item.id;
                /*
                  Knappen «Læs hele beskeden» skal kun stå der, hvis der ER
                  mere at læse. Tre linjer er omtrent 150 tegn på en telefon;
                  det er et skøn og ikke en måling, men en kort besked med et
                  «læs mere», der ikke afslører noget, er værre end ingen
                  knap.
                */
                const kanFoldesUd = item.body.length > 150 || item.body.includes('\n');
                return (
                  <article
                    key={item.id}
                    className={`card msg${read ? '' : ' msg--unread'}`}
                  >
                    {/*
                      HELE OVERSKRIFTEN OG SMUGKIGGET ER ÉN KNAP.
                      Designguiden beder om, at kortet er trykbart. Et helt
                      kort med «Svar» og «Markér som læst» inden i sig kan
                      ikke være det: en knap inden i en knap er hverken til
                      at ramme eller at forstå for en skærmlæser. Derfor er
                      det ALT det, man læser, der er trykfladen — og
                      handlingerne står uden for den.
                    */}
                    <button
                      type="button"
                      className="msg__open"
                      aria-expanded={kanFoldesUd ? expanded : undefined}
                      disabled={!kanFoldesUd}
                      aria-controls={`beskedtekst-${item.id}`}
                      onClick={() => setOpenMessage(expanded ? null : item.id)}
                    >
                      <span className="msg__head">
                        <span className="msg__title">{item.title}</span>
                        <span className="msg__badges">
                          {item.priority === 'important' ? (
                            <Badge kind="important">Vigtig</Badge>
                          ) : null}
                          {!read ? <Badge kind="unread">Ny</Badge> : null}
                        </span>
                      </span>
                      <span className="msg__meta">
                        {item.audienceLabel} · {relativeDay(item.publishedAt)} ·{' '}
                        {item.sentByMe ? 'Sendt af dig' : `Fra ${item.senderName}`}
                      </span>
                      {/*
                        Smugkigget er klippet til tre linjer med CSS og ikke
                        med slice(). Så er der aldrig et ord, der bliver
                        hugget midt over, og hele teksten er der allerede,
                        når kortet foldes ud.
                      */}
                      <span
                        id={`beskedtekst-${item.id}`}
                        className={expanded ? 'msg__body' : 'msg__body msg__body--clamped'}
                      >
                        {item.body}
                      </span>
                      {kanFoldesUd ? (
                        <span className="msg__toggle">
                          {expanded ? 'Vis mindre' : 'Læs hele beskeden'}
                        </span>
                      ) : null}
                    </button>

                    {/*
                      HANDLINGERNE STÅR ALTID FREMME.
                      Første forsøg lagde dem bag udfoldningen, og en test
                      fangede det: så kunne man ikke svare uden først at
                      folde beskeden ud. Det er også forkert i sig selv —
                      man skal kunne kvittere for en besked, man har læst
                      overskriften på, uden at åbne den.

                      Udfoldningen gør derfor kun ÉN ting: den fjerner
                      klipningen af teksten.
                    */}
                    <div className="msg__panel">
                      <p className="msg__actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => void toggleRead(item)}
                        >
                          <Icon name={read ? 'message' : 'attendance'} size={17} />
                          {read ? 'Markér som ulæst' : 'Markér som læst'}
                        </button>
                      </p>
                      <ReplySection item={item} onChanged={state.reload} />
                    </div>
                  </article>
                );
              })
            )}
            <p className="tiny muted">{data.note}</p>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Svar til afsenderen                                                 */
/* ------------------------------------------------------------------ */

interface Reply {
  id: string;
  fromName: string;
  body: string;
  createdAt: string;
  read: boolean;
  mine: boolean;
}

/**
 * Svar går KUN til afsenderen.
 *
 * Det står der også på skærmen. Folk skriver anderledes, når de tror, hele
 * holdet kigger med — og en forælder skal turde spørge om noget, der handler
 * om hans barn.
 */
function ReplySection({ item, onChanged }: { item: InboxItem; onChanged: () => void }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const action = useAction();
  const replies = useAsync<{
    replies: Reply[];
    canReply: boolean;
    cannotReplyReason: string;
  }>(
    () =>
      open
        ? api.get(`/api/announcements/${encodeURIComponent(item.id)}/replies`)
        : Promise.resolve({ replies: [], canReply: false, cannotReplyReason: '' }),
    [open, item.id],
  );

  const openThread = (): void => {
    setOpen(true);
    // Afsenderen har nu set svarene. Markeringen er en bekvemmelighed —
    // fejler den, sker der ikke noget alvorligt.
    if (item.sentByMe && item.unreadReplies > 0) {
      void api
        .post(`/api/announcements/${encodeURIComponent(item.id)}/replies/read`)
        .then(onChanged)
        .catch(() => undefined);
    }
  };

  const send = (): void => {
    void action.run(async () => {
      await api.post(`/api/announcements/${encodeURIComponent(item.id)}/replies`, { body: text });
      action.setSuccess('Svaret er sendt til afsenderen. Ingen andre kan se det.');
      setText('');
      replies.reload();
      onChanged();
    });
  };

  const canOpen = item.repliesEnabled || item.replyCount > 0;
  if (!canOpen) {
    return item.sentByMe ? (
      <p className="tiny muted mt-1">
        Du slog svar fra på denne besked.
      </p>
    ) : null;
  }

  if (!open) {
    return (
      <p className="mt-1">
        <button type="button" className="btn btn--ghost btn--small" onClick={openThread}>
          {item.sentByMe
            ? item.replyCount === 0
              ? 'Ingen svar endnu'
              : `${item.replyCount} ${item.replyCount === 1 ? 'svar' : 'svar'}${
                  item.unreadReplies > 0 ? ` · ${item.unreadReplies} nye` : ''
                }`
            : item.replyCount > 0
              ? 'Se dit svar'
              : 'Svar afsenderen'}
        </button>
      </p>
    );
  }

  return (
    <div className="reply mt-3">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={replies} skeletonLines={2}>
        {(data) => (
          <>
            {data.replies.length > 0 ? (
              <ul className="list" style={{ marginBottom: 12 }}>
                {data.replies.map((reply) => (
                  <li key={reply.id} className="reply__item">
                    <p className="tiny muted">
                      {reply.mine ? 'Dit svar' : reply.fromName} · {relativeDay(reply.createdAt)}
                    </p>
                    <p className="small" style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>
                      {reply.body}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}

            {data.canReply ? (
              <>
                <Field
                  label="Svar til afsenderen"
                  hint="Kun afsenderen kan se dit svar — ikke resten af holdet."
                  htmlFor={`reply-${item.id}`}
                >
                  <textarea
                    id={`reply-${item.id}`}
                    rows={3}
                    maxLength={REPLY_BODY_MAX}
                    value={text}
                    onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
                    placeholder="Skriv dit spørgsmål …"
                  />
                </Field>
                <div className="btn-group">
                  <ActionButton
                    pending={action.pending}
                    disabled={text.trim().length === 0}
                    onClick={send}
                  >
                    Send svar
                  </ActionButton>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setOpen(false)}
                  >
                    Luk
                  </button>
                </div>
              </>
            ) : (
              <div className="row row--between">
                <p className="tiny muted">
                  {data.cannotReplyReason ||
                    (item.sentByMe
                      ? 'Skriv en privat besked, hvis du vil svare tilbage.'
                      : 'Der kan ikke svares på denne besked.')}
                </p>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => setOpen(false)}
                >
                  Luk
                </button>
              </div>
            )}
          </>
        )}
      </AsyncSection>
    </div>
  );
}
